import { create } from "zustand";
import { ArtworkWithTexture } from "../types";
import _ from "lodash"; // Ensure lodash is imported
import { parse } from "raji";
import audioManager, { AudioManager } from "../audio";
import { Texture } from "pixi.js";

// Define the interface for the store state and actions
interface StoreState {
  // Pagination
  currentPage: number;
  incrementPage: () => void;
  setCurrentPage: (page: number) => void;

  // Collection State
  hasReachedEnd: boolean;
  setHasReachedEnd: (state: boolean) => void;

  // Artwork Selection
  selectedArtwork: ArtworkWithTexture | null;
  setSelectedArtwork: (artwork: ArtworkWithTexture | null) => void;

  // Artwork Movement
  artworkInFront: boolean;
  setArtworkInFront: (state: boolean) => void;

  // Loading State
  isLoading: boolean;
  setIsLoading: (state: boolean) => void;

  // Artwork History
  addArtworkToHistory: (artwork: ArtworkWithTexture) => void;
  clearArtworkViewHistory: () => void;

  // Selected Tags
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;

  // Sound enabled
  soundEnabled: boolean;
  setSoundEnabled: (state: boolean) => void;

  // Audio Manager
  audioManager: AudioManager | null;
  setAudioManager: (audioManager: AudioManager) => void;

  // Tour State
  isTourOpen: boolean;
  setTourOpen: (state: boolean) => void;

  // Info Modal
  isInfoOpen: boolean;
  setInfoOpen: (state: boolean) => void

  // Combined Textures
  combinedTextures: Record<string, Texture>;
  setCombinedTextures: (textures: Record<string, Texture>) => void;
  clearCombinedTextures: () => void;
}

// Create the Zustand store with organized state and actions
export const useStore = create<StoreState>((set, get) => ({
  // Pagination
  currentPage: 1,
  incrementPage: () => set({ currentPage: get().currentPage + 1 }),
  setCurrentPage: (page) => set({ currentPage: page }),

  // Collection State
  hasReachedEnd: false,
  setHasReachedEnd: (state) => set({ hasReachedEnd: state }),

  // Artwork Selection
  selectedArtwork: null,
  setSelectedArtwork: (artwork) => set({ selectedArtwork: artwork }),

  // Artwork Movement
  artworkInFront: false,
  setArtworkInFront: (state) => set({ artworkInFront: state }),

  // Loading State
  isLoading: false,
  setIsLoading: (state) => set({ isLoading: state }),

  // Selected Tags
  selectedTags: [],
  setSelectedTags: (tags) => set({ selectedTags: tags }),

  // Sound enabled
  soundEnabled: true,
  setSoundEnabled: (state) => set({ soundEnabled: state }),

  // Audio Manager
  audioManager: null,
  setAudioManager: (audioManager) => set({ audioManager }),

  // Tour State
  isTourOpen: false,
  setTourOpen: (state) => set({ isTourOpen: state }),

  isInfoOpen: false,
  setInfoOpen: (state) => set({ isInfoOpen: state }),

  // Combined Textures
  combinedTextures: {},
  setCombinedTextures: (textures) => set({ combinedTextures: textures }),
  clearCombinedTextures: () => set({ combinedTextures: {} }),

  // Artwork History
  addArtworkToHistory: (artwork) => {
    // Execute the operation asynchronously to avoid blocking
    setTimeout(async () => {
      const historyKey = "artworkViewHistory";
      const localStorage = window.localStorage;

      // Retrieve existing history
      let artworkHistory: ArtworkWithTexture[] = [];
      const existingHistory = localStorage.getItem(historyKey);

      if (existingHistory) {
        try {
          artworkHistory = await parse(existingHistory);
        } catch (error) {
          console.error(
            "Failed to parse artworkViewHistory from localStorage:",
            error,
          );
          // If parsing fails, reset the history
          artworkHistory = [];
        }
      }

      // Check if the artwork already exists in history
      const existingArtwork = _.find(artworkHistory, { uuid: artwork.uuid });

      if (!existingArtwork) {
        // If not, add it to the history
        artworkHistory.push(artwork);
      } else {
        // If it exists, reorder it to the end
        const index = artworkHistory.findIndex(
          (item) => item.uuid === artwork.uuid,
        );
        if (index !== -1) {
          const [currentArtwork] = artworkHistory.splice(index, 1);
          artworkHistory.push(currentArtwork);
        }
      }

      // Save the updated history back to localStorage
      try {
        localStorage.setItem(historyKey, JSON.stringify(artworkHistory));
      } catch (error) {
        console.error(
          "Failed to set artworkViewHistory in localStorage:",
          error,
        );
      }
    }, 0); // Delay of 0 to push the operation to the event loop
  },
  clearArtworkViewHistory: () => {
    const historyKey = "artworkViewHistory";
    const localStorage = window.localStorage;
    try {
      localStorage.removeItem(historyKey);
      console.log("Artwork view history cleared.");
    } catch (error) {
      console.error(
        "Failed to clear artworkViewHistory from localStorage:",
        error,
      );
    }
  },
}));
